import React, { useState, useCallback, useEffect } from 'react';
import {
    KatModal,
    KatInput,
    KatButton,
    KatRadiobuttonGroup,
    KatAlert,
    KatRow,
    KatColumn,
    KatDropdown,
    KatSpinner,
} from '@amzn/katal-react';
import { useSpaces } from '../contexts';
import { ApiClientError, templatesApi } from '../api/client';
import { TemplateSummary } from '../types';

interface NewSpaceDialogProps {
    visible: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

type Visibility = 'private' | 'internal';

/**
 * Dialog component for creating a new space
 *
 * Renders a modal with form fields for space name, template selection, and visibility.
 * Handles form validation and API submission.
 */
const NewSpaceDialog: React.FC<NewSpaceDialogProps> = ({
    visible,
    onClose,
    onSuccess,
}) => {
    const { createSpace } = useSpaces();
    const [name, setName] = useState('');
    const [visibility, setVisibility] = useState<Visibility>('private');
    const [templateId, setTemplateId] = useState<string>('blank');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [templates, setTemplates] = useState<TemplateSummary[]>([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    // Fetch templates when dialog opens
    useEffect(() => {
        if (visible && templates.length === 0) {
            setLoadingTemplates(true);
            templatesApi
                .list()
                .then((result) => {
                    setTemplates(result);
                })
                .catch((err) => {
                    console.error('Failed to fetch templates:', err);
                    // Don't show error, just use empty templates list
                })
                .finally(() => {
                    setLoadingTemplates(false);
                });
        }
    }, [visible, templates.length]);

    // Reset form state when dialog is closed
    const handleClose = useCallback(() => {
        setName('');
        setVisibility('private');
        setTemplateId('blank');
        setError(null);
        setSubmitting(false);
        onClose();
    }, [onClose]);

    // Handle form submission
    const handleSubmit = useCallback(async () => {
        // Validate name
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError('Space name is required');
            return;
        }

        if (trimmedName.length > 100) {
            setError('Space name must be 100 characters or less');
            return;
        }

        setError(null);
        setSubmitting(true);

        try {
            await createSpace(trimmedName, visibility, templateId);
            handleClose();
            onSuccess?.();
        } catch (err) {
            const message =
                err instanceof ApiClientError
                    ? err.message
                    : 'Failed to create space';
            setError(message);
        } finally {
            setSubmitting(false);
        }
    }, [name, visibility, templateId, createSpace, handleClose, onSuccess]);

    // Handle name input change
    const handleNameChange = useCallback((event: CustomEvent) => {
        setName(event.detail.value);
        // Clear error when user starts typing
        if (error) {
            setError(null);
        }
    }, [error]);

    // Handle visibility change
    const handleVisibilityChange = useCallback((event: CustomEvent) => {
        setVisibility(event.detail.value as Visibility);
    }, []);

    // Handle template change
    const handleTemplateChange = useCallback((event: CustomEvent) => {
        setTemplateId(event.detail.value as string);
    }, []);

    // Handle form key press for Enter to submit
    const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !submitting) {
            handleSubmit();
        }
    }, [handleSubmit, submitting]);

    // Build template options for dropdown
    const templateOptions = templates.map((t) => ({
        value: t.templateId,
        label: t.name,
        name: 'template',
    }));

    if (!visible) {
        return null;
    }

    return (
        <KatModal
            visible={visible}
            onClose={handleClose}
            title="Create New Space"
        >
            <div
                className="new-space-dialog"
                onKeyPress={handleKeyPress}
                role="form"
                aria-label="Create new space form"
            >
                {error && (
                    <KatAlert
                        variant="danger"
                        header="Error"
                        description={error}
                    />
                )}

                <KatRow>
                    <KatColumn xs={12}>
                        <KatInput
                            label="Space Name"
                            value={name}
                            onChange={handleNameChange}
                            placeholder="Enter a name for your space"
                            required
                            disabled={submitting}
                            maxlength={100}
                            constraintLabel="Required. Max 100 characters."
                        />
                    </KatColumn>
                </KatRow>

                <KatRow>
                    <KatColumn xs={12}>
                        {loadingTemplates ? (
                            <div className="template-loading" data-testid="template-loading">
                                <KatSpinner size="small" />
                                <span>Loading templates...</span>
                            </div>
                        ) : (
                            <KatDropdown
                                label="Template"
                                value={templateId}
                                options={templateOptions}
                                onChange={handleTemplateChange}
                                disabled={submitting || loadingTemplates}
                                searchable={false}
                                constraintLabel="Choose a starter template for your space"
                                data-testid="template-dropdown"
                            />
                        )}
                    </KatColumn>
                </KatRow>

                <KatRow>
                    <KatColumn xs={12}>
                        <KatRadiobuttonGroup
                            label="Visibility"
                            name="visibility"
                            value={visibility}
                            onChange={handleVisibilityChange}
                            options={[
                                {
                                    value: 'private',
                                    label: 'Private',
                                    constraintLabel: 'Only you can access this space',
                                    name: 'visibility',
                                },
                                {
                                    value: 'internal',
                                    label: 'Internal',
                                    constraintLabel: 'All authenticated users can access this space',
                                    name: 'visibility',
                                },
                            ]}
                        />
                    </KatColumn>
                </KatRow>

                <KatRow>
                    <KatColumn xs={12}>
                        <div className="new-space-dialog-actions">
                            <KatButton
                                variant="secondary"
                                label="Cancel"
                                onClick={handleClose}
                                disabled={submitting}
                            />
                            <KatButton
                                variant="primary"
                                label={submitting ? 'Creating...' : 'Create Space'}
                                onClick={handleSubmit}
                                disabled={submitting || !name.trim()}
                                loading={submitting}
                            />
                        </div>
                    </KatColumn>
                </KatRow>
            </div>
        </KatModal>
    );
};

export default NewSpaceDialog;
